import * as Icons from '@fox/icons';
import { IconProps as MuiIconProps } from '@mui/material/Icon';
import SvgIcon from '@mui/material/SvgIcon';
import React, { CSSProperties } from 'react';

export type IconName = keyof typeof Icons;

export function isIconName(name: string | null | undefined): boolean {
	if (!name) return false;
	return Icons[name];
}

export interface IconProps extends Pick<MuiIconProps, 'color' | 'sx' | 'fontSize' | 'title'> {
	iconName: IconName;
	/** This property should only be used in rare circumstances where the common theme colors aren't sufficient.*/
	customColor?: CSSProperties['color'];
}

// Unfortunately, gov insights has icon names stored in the data.
// This means we would need to either change the data or use this map to get the correct icon :(
const GOV_INSIGHTS_ICON_MAP = {
	'age-profile': 'AgeProfile',
	'cultural-diversity': 'CulturalDiversity',
	education: 'Education',
	wealth: 'Wealth',
	'jobs-sector': 'JobsSector',
	jobs: 'Jobs',
	property: 'Property',
	religion: 'Public',
	transport: 'Transport'
} as const;

/** This is an extension of the Mui Icon component, with a handy `iconName` prop that allows you to pass in the name of the desired icon.
 * The `iconName` prop is strongly typed and will error if you do not provide the name of an icon that is exported from the @fox/icons module.
 * You can also recieve type hints for a list of available icons. If an incorrect name somehow makes it to production, a question mark icon
 * will be rendered as a fallback. Custom .svg files in the @fox/icons module will automatically have their viewbox scaled to fit the Icon component.
 * See @fox/icons documentation for more details about adding new custom icons.
 */
export const Icon = React.forwardRef<HTMLButtonElement, IconProps>(({ iconName, color, customColor, ...rest }, ref) => {
	const name = GOV_INSIGHTS_ICON_MAP[iconName] ?? iconName;
	const IconComponent = Icons[name] ?? Icons.QuestionMark;

	return (
		<SvgIcon
			ref={ref}
			{...rest}
			color={customColor ? undefined : color}
			style={{ color: customColor }}
			component={IconComponent}
			inheritViewBox
		/>
	);
});
